import React from 'react';

import { Lock } from 'lucide-react';

import {
	useGetPricingLimitSettingsQuery,
	useUpdatePricingLimitSettingsMutation,
} from '../../api/pricingApprovals';
import { useFeature } from '../../hooks';
import { useAuth } from '../../providers/AppAuthProvider';

import type { PricingLimitAction } from '../../api/pricingApprovals';

const OPTIONS: { value: PricingLimitAction; label: string; help: string }[] = [
	{
		value: 'route',
		label: 'Route for approval',
		help: 'The quote saves as a draft and goes to the next approver.',
	},
	{
		value: 'block',
		label: 'Block the save',
		help: 'The quote cannot be saved until every value is inside its limits.',
	},
];

// v9 settings-revenue "Pricing limits" (#prLimitCard): what happens when a quote breaks a limit.
const PricingLimitsCard: React.FC = () => {
	const enabled = useFeature('approvals');
	const { isAdmin } = useAuth();
	const { data } = useGetPricingLimitSettingsQuery(undefined, {
		skip: !enabled,
	});
	const [update, { isLoading }] = useUpdatePricingLimitSettingsMutation();

	if (!enabled) return null;
	const action = data?.action ?? 'route';
	const disabled = !isAdmin || isLoading || !data;
	const help = OPTIONS.find((o) => o.value === action)?.help;

	return (
		<section className="rounded-lg border border-gray-200 bg-white">
			<div className="flex flex-wrap items-center justify-between gap-4 px-5 pb-2.5 pt-3.5">
				<div className="min-w-0">
					<h2 className="flex items-center gap-2 text-base font-medium text-gray-900">
						Pricing limits
						<span className="ml-2 inline-flex h-5 items-center rounded-full border border-blue-500 bg-blue-50 px-2 text-[11px] font-semibold tracking-[.02em] text-blue-800">
							New
						</span>
						{!isAdmin && (
							<span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 px-2 py-1 text-xs font-medium leading-[1.3] text-gray-600">
								<Lock className="h-3 w-3" />
								Admin only
							</span>
						)}
					</h2>
					<p className="mt-0.5 text-sm text-gray-500">
						When a quote breaks a floor, a ceiling or the deal margin floor
					</p>
				</div>
				<fieldset
					aria-label="When a quote breaks a limit"
					disabled={disabled}
					className="m-0 inline-flex flex-wrap gap-0.5 rounded-full border border-gray-200 bg-white p-[3px]"
				>
					{OPTIONS.map((o) => {
						const on = action === o.value;
						return (
							<label
								key={o.value}
								className={`relative inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-medium focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-blue-500 ${
									on
										? 'bg-primary-500 text-white'
										: 'text-gray-700 hover:bg-gray-100'
								} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
							>
								<input
									type="radio"
									name="pricing-limit-action"
									value={o.value}
									checked={on}
									disabled={disabled}
									onChange={() => update({ action: o.value })}
									className="sr-only"
								/>
								<span>
									{o.label}
									{o.value === 'route' && (
										<span className={on ? 'text-white/75' : 'text-gray-500'}>
											{' '}
											· default
										</span>
									)}
								</span>
							</label>
						);
					})}
				</fieldset>
			</div>
			<p className="m-0 px-5 pb-3.5 text-xs text-gray-600">
				{help} A proposal with a broken limit cannot be released before
				approval.
				{!isAdmin && ' Only an admin can change this setting.'}
			</p>
		</section>
	);
};

export default PricingLimitsCard;
